import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Message, Composer, ConversationHeader, SensitivePanel } from "../../components/company/Conversation";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { useCompany } from "../../components/layout/CompanyLayout";
import { Loader, ErrorState } from "../../components/common/States";
import { StatusBadge, TypeBadge, SentimentBadge, Stars, VisibilityBadge, PublicationBadge } from "../../components/common/Badges";
import { CATEGORY, CHANNEL, fmtDate } from "../../lib/labels";
import { openAttachment, fmtSize } from "../../lib/files";
import { FileText } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import ReasonDialog from "../../components/admin/ReasonDialog";
import { SectorAnswers } from "../../components/feedback/SectorFields";
import { ProcessingPanel, DatesPanel, ActionsPanel, HistoryPanel, ReportPanel } from "../../components/company/FeedbackPanels";

const Moderation = ({ fb, onSaved }) => {
  const [rejecting, setRejecting] = useState(false);
  const decide = async (decision, reason = "") => {
    try { const { data } = await api.post(`/company/feedbacks/${fb.id}/publication`, { decision, reason }); onSaved(data); setRejecting(false); toast.success(decision === "approved" ? "Avis publié" : "Publication refusée"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (fb.moderation_hidden) return null;
  return (
    <div className="card-surface flex flex-wrap items-center gap-3 p-5" data-testid="moderation-card">
      <p className="flex-1 text-sm text-slate-600">Avis public : {fb.publication === "approved" ? "visible sur votre fiche publique (sans coordonnées)." : fb.publication === "rejected" ? "non publié." : "en attente de votre validation."}</p>
      {fb.publication !== "approved" && <Button size="sm" onClick={() => decide("approved")} data-testid="moderation-approve">Publier</Button>}
      {fb.publication !== "rejected" && <Button size="sm" variant="outline" onClick={() => setRejecting(true)} data-testid="moderation-reject">{fb.publication === "approved" ? "Retirer" : "Refuser"}</Button>}
      {fb.publication_reason && fb.publication === "rejected" && <p className="w-full text-xs text-slate-500" data-testid="publication-reason">Motif : {fb.publication_reason}</p>}
      {rejecting && <ReasonDialog testId="publication-reject" title="Refuser la publication" label="Confirmer le refus" placeholder="Motif (tracé dans l'historique). Une note basse ne justifie pas à elle seule un refus."
        onConfirm={(r) => decide("rejected", r)} onClose={() => setRejecting(false)} />}
    </div>
  );
};

const AiCard = ({ fb, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/company/feedbacks/${fb.id}/analyze`); onSaved(data); toast.success("Analyse IA mise à jour"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface p-5" data-testid="ai-summary-card">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-display font-semibold"><Sparkles className="h-4 w-4 text-violet-600" />Analyse IA</p>
        <Button variant="ghost" size="sm" onClick={run} disabled={busy} data-testid="ai-analyze-button">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : fb.ai ? "Relancer" : "Analyser"}</Button>
      </div>
      {fb.ai ? (
        <div className="mt-3 space-y-3 text-sm">
          <div className="flex flex-wrap gap-2"><SentimentBadge value={fb.ai.sentiment} /><span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs" data-testid="ai-category">{fb.ai.category}</span>
            {fb.ai.urgent && <span className="flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700"><AlertTriangle className="h-3 w-3" />Urgent</span>}</div>
          <p className="leading-relaxed text-slate-700" data-testid="ai-summary">{fb.ai.summary}</p>
          <p className="text-xs text-slate-400">Analysé le {fmtDate(fb.ai.analyzed_at)} · {fb.ai.model || "claude-sonnet-4-5"} · aide à la décision, aucune modification automatique</p>
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-500">{fb.ai_status === "pending" ? "Analyse en cours… actualisez dans quelques secondes." : fb.ai_error || (fb.ai_status === "error" ? "L'analyse a échoué. Relancez-la." : "Pas encore analysé.")}</p>
      )}
    </div>
  );
};

const Contact = ({ fb }) => (
    <div className="card-surface p-5">
      <div className="text-sm">
        <p className="eyebrow mb-2">Usager</p>
        {fb.anonymous ? <p className="text-slate-500">Dépôt anonyme</p> : (
          <div className="space-y-0.5 text-slate-700" data-testid="feedback-contact"><p className="font-medium">{fb.contact_name}</p>{fb.contact_email && <p>{fb.contact_email}</p>}{fb.contact_phone && <p>{fb.contact_phone}</p>}</div>
        )}
      </div>
    </div>
);

export default function FeedbackDetail() {
  const { id } = useParams();
  const { can } = useCompany();
  const { user } = useAuth();
  const { data: fb, loading, error, reload, setData } = useApi(`/company/feedbacks/${id}`);
  const team = useApi("/company/team");
  const agencies = useApi("/company/agencies");
  const services = useApi("/company/services");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const agency = agencies.data?.find((a) => a.id === fb.agency_id);
  const service = services.data?.find((s) => s.id === fb.service_id);
  return (
    <div>
      <Link to="/app/feedbacks" className="mb-6 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="feedback-back-link"><ArrowLeft className="h-4 w-4" />Retour aux feedbacks</Link>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card-surface p-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold" data-testid="feedback-detail-code">{fb.tracking_code}</span>
              <TypeBadge value={fb.type} /><StatusBadge value={fb.status} /><VisibilityBadge value={fb.visibility} />{fb.publication && <PublicationBadge value={fb.publication} />}<Stars value={fb.rating} />
              {fb.sensitive && <span className="flex items-center gap-1 rounded-full bg-rose-600 px-2 py-0.5 text-xs font-medium text-white" data-testid="sensitive-badge"><ShieldAlert className="h-3 w-3" />Sensible</span>}
            </div>
            <p className="mt-1 text-xs text-slate-500">{fmtDate(fb.created_at)} · <span data-testid="feedback-channel">{CHANNEL[fb.channel] || fb.channel}</span>{agency ? ` · ${agency.name}` : ""}{service ? ` · ${service.name}` : ""}{fb.category ? ` · ${fb.category_label || CATEGORY[fb.category] || fb.category}` : ""}{fb.experience_date ? ` · expérience du ${fmtDate(fb.experience_date, false)}` : ""}</p>
            {fb.title && <p className="mt-4 font-display font-semibold" data-testid="feedback-detail-title">{fb.title}</p>}
            <p className="mt-2 whitespace-pre-wrap leading-relaxed text-slate-800" data-testid="feedback-detail-message">{fb.message}</p>
            <SectorAnswers fb={fb} />
            {fb.attachments?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {fb.attachments.map((a) => (
                  <button key={a.id} onClick={() => openAttachment(`/company/feedbacks/${fb.id}/attachments/${a.id}`)} data-testid={`company-attachment-${a.id}`}
                    className="flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-xs text-blue-700 hover:underline"><FileText className="h-3.5 w-3.5" />{a.filename} ({fmtSize(a.size)})</button>
                ))}
              </div>
            )}
          </div>
          {fb.visibility === "public" && can("feedback:moderate") && <Moderation fb={fb} onSaved={setData} />}
          <ActionsPanel fb={fb} team={team.data} can={can} onSaved={setData} />
          <div className="space-y-3" data-testid="conversation-thread">
            <ConversationHeader fb={fb} can={can} onSaved={setData} />
            {!fb.responses.length && <p className="text-sm text-slate-500">Aucun message pour l'instant.</p>}
            {fb.responses.map((r) => <Message key={r.id} r={r} />)}
          </div>
          {can("feedback:respond") && <Composer fb={fb} onSaved={setData} />}
          <HistoryPanel fb={fb} />
        </div>
        <div className="space-y-6">
          <ProcessingPanel fb={fb} team={team.data} can={can} me={user?.id} onSaved={setData} />
          <SensitivePanel fb={fb} can={can} onSaved={setData} />
          <DatesPanel fb={fb} />
          <Contact fb={fb} />
          <ReportPanel fb={fb} can={can} onSaved={setData} />
          <AiCard fb={fb} onSaved={setData} />
        </div>
      </div>
    </div>
  );
}
